/**
 * Built-in Dynamic List Registry
 *
 * Some manifest parameters declare `x-ms-dynamic-list` with an `operationId`
 * that is not part of a connector swagger — it resolves to a portal-side
 * fetch (e.g. listing Foundry agents from the AI Foundry service).
 *
 * Resolvers register themselves here at import time and are consulted by
 * `useConnectorDynamicList` before falling through to the swagger pipeline.
 */

import type { DynamicOption } from './dynamicDataService';
import type { ConnectionReference } from '@/types/connectionReference';

export interface BuiltinDynamicListContext {
  /** Current values of all parameters in the node (for dependency resolution). */
  parameterValues: Record<string, unknown>;
  /** Connection reference for the node, if a connection is established. */
  connectionReference?: ConnectionReference | undefined;
  /** Acquire an Otto control-plane token. */
  getOttoToken: () => Promise<string | null>;
  /** Acquire an ARM token. */
  getArmToken: () => Promise<string | null>;
  /** Acquire a Foundry data-plane token (popup-based for first consent). */
  getFoundryToken: () => Promise<string | null>;
}

export interface BuiltinDynamicListResult {
  /** Resolved options. */
  options: DynamicOption[];
  /** Optional error message to surface in the UI. */
  error?: string;
  /**
   * Indicates an auth-related failure that could be recovered by re-authorizing
   * via an interactive popup. UI may render a dedicated "Authorize" affordance.
   */
  isAuthError?: boolean;
}

export type BuiltinDynamicListResolver = (
  ctx: BuiltinDynamicListContext
) => Promise<BuiltinDynamicListResult>;

const registry = new Map<string, BuiltinDynamicListResolver>();

export function registerBuiltinDynamicList(
  operationId: string,
  resolver: BuiltinDynamicListResolver
): void {
  registry.set(operationId, resolver);
}

export function getBuiltinDynamicListResolver(
  operationId: string
): BuiltinDynamicListResolver | undefined {
  return registry.get(operationId);
}

export function hasBuiltinDynamicListResolver(operationId: string): boolean {
  return registry.has(operationId);
}

/** Test-only: clear all registered resolvers. */
export function __clearBuiltinDynamicListRegistry(): void {
  registry.clear();
}
